import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { GenieAvatar } from "@/components/GenieAvatar";
import { ConversationTagPicker } from "./ConversationTagPicker";
import { Conversation } from "@/types/conversation/conversation";
import type { GenieIconName } from "@/components/ui/genie-icons/genie-icons";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

interface ConversationGenieIdentityCardProps {
  conversation: Conversation;
}

/**
 * Slim identity strip at the top of /genie-hub/conversations/:id. Reminds
 * the owner what this genie's job is before they read the call analysis.
 *
 * Signature gradient border + brand-tinted header per guidelines/design.md §6.
 * Tenant-branded only on the avatar (it IS the customer's genie). All
 * other chrome stays HG brand tokens.
 */
export function ConversationGenieIdentityCard({
  conversation,
}: ConversationGenieIdentityCardProps) {
  const agent = conversation.agent || {};
  const branding = agent.branding || {};
  const tenantPrimary = branding.primaryColor || "#4E9CFF";
  const tenantSecondary = branding.secondaryColor || tenantPrimary;
  const logoUrl = branding.logoUrl || branding.customAvatarUrl;

  const description: string | null = agent.description || null;
  const categoryRaw: string | null = agent.category || null;
  const category =
    categoryRaw && typeof categoryRaw === "string"
      ? categoryRaw.replace(/[-_]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
      : null;
  const voiceLabel: string | null = agent.voice_label || null;
  const captureLeads: boolean = Boolean(agent.capture_leads);
  const memoryEnabled: boolean = Boolean(agent.memory_enabled);
  const isDemo: boolean = Boolean(agent.is_demo_genie);
  const isMarketplace: boolean = Boolean(agent.is_marketplace_genie);

  const createdAt: string | null = agent.created_at || null;
  const createdDisplay = createdAt
    ? new Date(createdAt).toLocaleDateString("en-NZ", {
        month: "short",
        year: "numeric",
      })
    : null;

  const chips: { label: string; genieIcon: GenieIconName }[] = [];
  if (voiceLabel) chips.push({ label: voiceLabel, genieIcon: "microphone" });
  if (captureLeads) chips.push({ label: "Lead capture", genieIcon: "database" });
  if (memoryEnabled) chips.push({ label: "Memory", genieIcon: "brain" });
  if (isDemo) chips.push({ label: "Demo", genieIcon: "eye" });
  if (isMarketplace) chips.push({ label: "Marketplace", genieIcon: "share" });

  return (
    <div className="p-[2px] rounded-2xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] shadow-sm mb-6">
      <div
        className="rounded-[14px] bg-white overflow-hidden"
        style={{
          background:
            "linear-gradient(135deg, rgba(78,156,255,0.04) 0%, rgba(255,77,184,0.04) 100%), #FFFFFF",
        }}
      >
        <div className="px-5 md:px-6 py-5 flex items-center gap-5">
          {/* Avatar — tenant brand */}
          <div className="flex-shrink-0">
            {logoUrl ? (
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center"
                style={{
                  background: `radial-gradient(circle, ${tenantPrimary}15 0%, ${tenantPrimary}05 70%, transparent 100%)`,
                }}
              >
                <img
                  src={logoUrl}
                  alt={agent.name || "Genie"}
                  className="w-14 h-14 object-contain"
                  style={{ filter: `drop-shadow(0 0 6px ${tenantPrimary}40)` }}
                />
              </div>
            ) : (
              <GenieAvatar
                size={64}
                color={tenantPrimary}
                secondaryColor={tenantSecondary}
                gradientEnabled={branding.gradientEnabled !== false}
                gradientAngle={branding.gradientAngle || 135}
                avatarBackgroundOpacity={branding.avatarBackgroundOpacity}
                customAvatarUrl={logoUrl}
              />
            )}
          </div>

          {/* Identity */}
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-3 flex-wrap">
              <h2 className="text-xl font-bold text-gray-900 leading-tight">
                {agent.name || "Genie"}
              </h2>
              {category && (
                <span className="text-xs text-gray-500 font-medium uppercase tracking-wide">
                  {category}
                </span>
              )}
            </div>
            {description && (
              <p className="text-sm text-gray-600 mt-1 leading-relaxed line-clamp-2">
                {description}
              </p>
            )}
            {chips.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 mt-3">
                {chips.map((chip) => (
                  <span
                    key={chip.label}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
                    style={{
                      background:
                        "linear-gradient(90deg, rgba(78,156,255,0.08), rgba(255,77,184,0.08))",
                      border: "1px solid rgba(78,156,255,0.2)",
                      color: "#222B45",
                    }}
                  >
                    <GenieIcon
                      name={chip.genieIcon}
                      size={12}
                      color="#4E9CFF"
                      secondaryColor="#FF4DB8"
                      gradientEnabled
                      label={chip.label}
                    />
                    {chip.label}
                  </span>
                ))}
              </div>
            )}

            {/* Owner-side tags (HG product tagging, not tenant brand) */}
            <div className="mt-3">
              <ConversationTagPicker conversationId={conversation.id} />
            </div>
          </div>

          {/* Meta + edit shortcut */}
          <div className="hidden md:flex flex-col items-end gap-2 flex-shrink-0">
            {createdDisplay && (
              <div className="text-right">
                <div className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold">
                  Set up
                </div>
                <div className="text-sm font-semibold text-gray-900">{createdDisplay}</div>
              </div>
            )}
            <Link
              to={`/genie-hub/my-genies/${conversation.agent_id}`}
              className="inline-flex items-center gap-1 text-xs hover:gap-1.5 transition-all"
            >
              <span className="bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] bg-clip-text text-transparent font-semibold">
                Edit setup
              </span>
              <ArrowRight className="w-3 h-3 text-[#FF4DB8]" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
